import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";

const journalPosts = [
  {
    id: 1,
    title: "The Art of Moody Florals: Embracing Darkness in Design",
    excerpt: "Exploring how deep, rich tones and unconventional color palettes can transform traditional arrangements into contemporary art pieces.",
    date: "January 15, 2026",
    category: "Design Philosophy",
    image: "https://images.unsplash.com/photo-1647644520784-2a4bd03a4204?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBmbG9yYWwlMjBhcnJhbmdlbWVudHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 2,
    title: "Winter Blooms: A Guide to Seasonal Elegance",
    excerpt: "Discover the unexpected beauty of winter florals and learn how to incorporate them into your celebrations.",
    date: "January 8, 2026",
    category: "Seasonal Guide",
    image: "https://images.unsplash.com/photo-1760977606412-e357b1e21c60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbWluaW1hbGlzdCUyMGZsb3dlcnxlbnwwfHx8fDE3NzYyNjIwNjd8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 3,
    title: "Behind the Scenes: Creating a Wedding Installation",
    excerpt: "A glimpse into the creative process behind one of our most ambitious wedding floral designs to date.",
    date: "December 28, 2025",
    category: "Behind the Scenes",
    image: "https://images.unsplash.com/photo-1724847764267-12775ec49657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzc2MjYyMDM5fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 4,
    title: "The Language of Roses: Beyond the Red",
    excerpt: "Exploring the rich symbolism and lesser-known varieties of the world's most beloved flower.",
    date: "December 20, 2025",
    category: "Floral Education",
    image: "https://images.unsplash.com/photo-1655788174766-a63b2d4409d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxibGFjayUyMGJhY2tncm91bmQlMjByZWQlMjByb3NlfGVufDB8fHx8MTc3NjI2MjA2N3ww&ixlib=rb-4.1.0&q=85"
  }
];

export default function Journal() {
  return (
    <div className="page-transition pt-[72px]" data-testid="journal-page">
      {/* Header */}
      <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10">
        <div className="container-custom text-center">
          <p className="overline mb-4">Stories & Insights</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#f8f5f0] mb-4">
            The Journal
          </h1>
          <p className="font-body text-[#a19d98] max-w-2xl mx-auto">
            Explorations in floral design, behind-the-scenes glimpses, 
            and seasonal inspirations from our atelier.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="section bg-[#161616]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="image-zoom aspect-[4/3] overflow-hidden">
              <img
                src={journalPosts[0].image}
                alt={journalPosts[0].title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <p className="overline">{journalPosts[0].category}</p>
                <span className="text-[#b8966a]/30">|</span>
                <p className="font-sans text-xs text-[#a19d98] flex items-center gap-2">
                  <Calendar size={12} />
                  {journalPosts[0].date}
                </p>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-[#f8f5f0] mb-4">
                {journalPosts[0].title}
              </h2>
              <p className="font-body text-[#a19d98] mb-6 leading-relaxed">
                {journalPosts[0].excerpt}
              </p>
              <Link 
                to="#" 
                className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.15em] uppercase text-[#b8966a] hover:text-[#cfae82] transition-colors"
              >
                Read Article
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* All Posts */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {journalPosts.slice(1).map((post, index) => (
              <article 
                key={post.id} 
                className="group"
                data-testid={`journal-post-${index}`}
              >
                <div className="image-zoom aspect-[4/3] overflow-hidden mb-6 border border-[#b8966a]/10 group-hover:border-[#b8966a]/30 transition-all">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <p className="overline text-[10px]">{post.category}</p>
                  <span className="text-[#b8966a]/30">|</span>
                  <p className="font-sans text-[10px] text-[#a19d98]">
                    {post.date}
                  </p>
                </div>
                <h3 className="font-heading text-xl text-[#f8f5f0] mb-3 group-hover:text-[#b8966a] transition-colors">
                  {post.title}
                </h3>
                <p className="font-body text-sm text-[#a19d98] line-clamp-2">
                  {post.excerpt}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
